/**
 * Release-readiness states: what the shell says when something has gone wrong.
 *
 * The rules live here rather than inside the components so they can be tested without a DOM, and
 * so the copy for each failure is decided in one place. The guiding principle throughout: never
 * claim something is happening that is not. A banner that says "Retrying…" when nothing retries is
 * worse than one that says "Lost" and offers a button.
 */
import type { ConnectionState, JoinFailureKind } from '../store/store';

/* --------------------------------------------------------- connection */

export type BannerTone = 'warn' | 'error';

export interface ConnectionBanner {
  text: string;
  /**
   * Present when the player can do something about it, and only ever the thing that actually helps:
   * `retry` re-opens a connection that dropped, `lobby` walks away from a code that will never work.
   * Offering `retry` for a refused code was the old bug — the button had no code to retry with.
   */
  action?: 'retry' | 'lobby';
  tone: BannerTone;
  /** Screen-reader urgency: a lost connection interrupts, a reconnect does not. */
  live: 'polite' | 'assertive';
}

/**
 * What to show for the current connection, or null when there is nothing to say.
 *
 * `online` comes from `navigator.onLine`, which is only trustworthy in the negative: false really
 * does mean no network, while true only means an interface exists. So it is used to *explain* a
 * failure, never to predict one.
 */
export function connectionBanner(opts: {
  connection: ConnectionState;
  online: boolean;
  /** `driver.lostReason` — currently only ever set for `replaced`. */
  lostReason?: string | null;
  /** `session.joinFailure`, the code that was refused and why. */
  joinFailure?: { code: string; kind: JoinFailureKind } | null;
}): ConnectionBanner | null {
  const { connection, online, lostReason, joinFailure } = opts;

  // A refused code outranks even the offline rule. The server answered to say the table is not
  // there, so the network is demonstrably working and "You're offline" would replace a fact with a
  // worse guess. The only useful action is to stop trying this code.
  if (connection === 'joinFailed') {
    return {
      text: joinFailure ? joinFailureText(joinFailure.code, joinFailure.kind) : 'That table could not be joined.',
      action: 'lobby',
      tone: 'error',
      live: 'assertive',
    };
  }

  // Offline outranks the rest: retrying is pointless and "Try again" would just fail.
  if (!online && (connection === 'reconnecting' || connection === 'lost')) {
    return { text: "You're offline. We'll pick up when you're back.", tone: 'warn', live: 'polite' };
  }

  if (connection === 'reconnecting') {
    return { text: 'Reconnecting…', tone: 'warn', live: 'polite' };
  }

  if (connection === 'lost') {
    // The server closed this tab because the same player joined from another one. That is not a
    // fault and a Try again here would fight the other tab for the seat.
    if (lostReason && /another tab|replaced/i.test(lostReason)) {
      return { text: 'Playing in another tab.', tone: 'warn', live: 'polite' };
    }
    return { text: 'Connection lost.', action: 'retry', tone: 'error', live: 'assertive' };
  }

  return null;
}

/* -------------------------------------------------------------- errors */

/** Cap on what leaves the browser with a crash report. */
export const MAX_ERROR_BYTES = 2048;

export interface ErrorReport {
  message: string;
  stack: string;
  /** Where in the app it happened, when React tells us. */
  component?: string;
}

/**
 * Trim a crash to something safe to send.
 *
 * A stack can be tens of kilobytes and can carry query strings from source URLs, so it is cut to
 * `MAX_ERROR_BYTES` measured in real UTF-8 bytes, not characters. The message is kept whole where
 * possible because it is the part a human reads first.
 */
export function toErrorReport(error: unknown, componentStack?: string | null): ErrorReport {
  const err = error instanceof Error ? error : new Error(String(error));
  const message = clampBytes(err.message || 'Unknown error', 300);
  const stack = clampBytes(err.stack ?? '', MAX_ERROR_BYTES - message.length);
  const report: ErrorReport = { message, stack };
  const component = componentStack ? clampBytes(componentStack.trim(), 400) : '';
  if (component) report.component = component;
  return report;
}

/**
 * Truncate to at most `maxBytes` of UTF-8, never leaving a broken character.
 *
 * Cuts the encoded bytes and walks back to a character boundary, rather than dropping one
 * character at a time and re-encoding to check. The naive version is O(n²) and took **8.8 seconds**
 * on a 50 kB stack, which is exactly the input this gets and exactly the worst moment to spend it:
 * it runs inside the crash handler, after something has already gone wrong.
 */
export function clampBytes(text: string, maxBytes: number): string {
  if (maxBytes <= 0 || text.length === 0) return '';
  const bytes = new TextEncoder().encode(text);
  if (bytes.length <= maxBytes) return text;

  const ellipsis = 3; // '…' in UTF-8
  let end = Math.max(0, maxBytes - ellipsis);
  // UTF-8 continuation bytes are 10xxxxxx; step back off one to land on a character start.
  while (end > 0 && (bytes[end]! & 0xc0) === 0x80) end--;

  const out = new TextDecoder().decode(bytes.subarray(0, end));
  return out.length > 0 ? `${out}…` : '';
}

/* --------------------------------------------------------- edge copy */

/**
 * Why an unseated player is watching, in the words that answer their actual question.
 *
 * "Table full" is the common case and the one most likely to read as a bug: four humans are
 * already sitting, this is a fifth, and nothing is broken.
 */
export function spectatorReason(opts: { seatedHumans: number; phase: string | undefined }): string {
  if (opts.seatedHumans >= 4) return 'Table full — you’re watching. Take the next free seat.';
  if (opts.phase && opts.phase !== 'lobby') return 'Watching this hand. Sit in for the next one.';
  return 'Watching.';
}

/** Copy for a code the player typed or followed that did not work. */
export function joinFailureText(raw: string, kind: 'malformed' | 'unknown'): string {
  return kind === 'malformed'
    ? `"${raw}" is not a table code. They are 5 letters and numbers.`
    : `No table with the code ${raw.toUpperCase()}. It may have finished.`;
}
